import { CardFooter, Typography } from "@material-tailwind/react";
import Label from "components/Label/Label";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import Input from "shared/Input/Input";
import DashboardMenu from "../../DashboardPage/DashboardMenu";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { useNavigate, useParams } from "react-router-dom";
import { TrashIcon } from "@heroicons/react/24/outline";
import { getVideoSliderById, updateVideoSlider } from "redux/features/Admin/videoSlider/videoSliderSlice";

export interface UpdateVideoPageProps {
  className?: string;
}

const MAX_FILE_SIZE = 1024 * 1024 * 2000;
const ACCEPTED_VIDEO_MIME_TYPES = ["video/mp4", "video/mpeg", "video/webm", "video/mov"];
const ACCEPTED_IMAGE_MIME_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp"];

const formSchema = z.object({
  title: z.string().min(1, { message: "Le Titre est obligatoire." }),
  file: z
    .instanceof(File)
    .optional()
    .refine((file) => !file || file.size <= MAX_FILE_SIZE, `La taille maximale video est de 2000MB.`)
    .refine(
      (file) => !file || ACCEPTED_VIDEO_MIME_TYPES.includes(file.type),
      "Le fichier doit être un video au format MP4, MPEG, WEBM ou MOV."
    ),
  image: z
    .instanceof(File)
    .optional()
    .refine((file) => !file || file.size <= MAX_FILE_SIZE, `La taille maximale d'image est de 10MB.`)
    .refine(
      (file) => !file || ACCEPTED_IMAGE_MIME_TYPES.includes(file.type),
      "Le fichier doit être une image au format JPEG, PNG, GIF ou WEBP."
    ),
});

type FormData = z.infer<typeof formSchema>;

const UpdateVideoSliderPage: FC<UpdateVideoPageProps> = ({ className = "" }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
    trigger,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onSubmit",
  });

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const videoState = useAppSelector((state) => state.videoHome);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [existingVideoUrl, setExistingVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      dispatch(getVideoSliderById(id)).then((result) => {
        const videoData = result.payload;
        if (videoData) {
          setValue("title", videoData.title);
          setExistingVideoUrl(videoData.file);
          setExistingImageUrl(videoData.image);
        }
      });
    }
  }, [id, dispatch, setValue]);

  useEffect(() => {
    switch (videoState.loading) {
      case "pending":
        setIsLoading(true);
        break;
      case "succeeded":
        setIsLoading(false);
        break;
      case "failed":
        setIsLoading(false);
        break;
    }
  }, [videoState.loading]);

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    if (!id) return;

    // Wrap the form data in a 'data' property
    const payload = {
      data: {
        ...data,
      },
      id,
    };

    await dispatch(updateVideoSlider(payload)).then(() => {
      navigate("/admin/dashboard/settings/slider-videos");
    });
  };

  const handleDeleteImage = () => {
    setValue("image", undefined);
    setExistingImageUrl(null);
  };

  const newImageFile = getValues("image");

  return (
    <div className={`nc-AccountPage ${className}`} data-nc-id="AccountPage">
      <Helmet>
        <title>EventToBee | Location d'espaces : salles, lofts, jardins & boutiques – Paris & Île-de-France</title>
      </Helmet>
      <DashboardMenu>
        <form onSubmit={handleSubmit(onSubmit)}>
          <span className="grid mb-4 place-items-center h-28"></span>
          <br />
          <Label>Titre du vidéo</Label>
          <br />
          <Input {...register("title")} name="title" placeholder="Titre du video" />
          {errors.title?.message && <div className="text-xs text-red-500">{errors.title.message}</div>}

          <Label>Vidéo</Label>
          <div className="mt-3">
            {existingVideoUrl && (
              <div className="mb-2">
                <p className="text-sm text-green-500">Vidéo actuelle:</p>
                <a
                  href={`${process.env.REACT_APP_CLOUD_FRONT}${existingVideoUrl}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600"
                >
                  Voir la vidéo
                </a>
              </div>
            )}
            <input
              className="relative m-0 block w-full min-w-0 flex-auto rounded border border-solid border-neutral-300 bg-clip-padding px-3 py-[0.32rem] text-base font-normal text-neutral-700 transition duration-300 ease-in-out file:-mx-3 file:-my-[0.32rem] file:overflow-hidden file:rounded-none file:border-0 file:border-solid file:border-inherit file:bg-neutral-100 file:px-3 file:py-[0.32rem] file:text-neutral-700 file:transition file:duration-150 file:ease-in-out file:[border-inline-end-width:1px] file:[margin-inline-end:0.75rem] hover:file:bg-neutral-200 focus:border-primary focus:text-neutral-700 focus:shadow-te-primary focus:outline-none dark:border-neutral-600 dark:text-neutral-200 dark:file:bg-neutral-700 dark:file:text-neutral-100 dark:focus:border-primary"
              type="file"
              id="formFile"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setValue("file", file);
                  trigger("file");
                }
              }}
              accept="video/mp4, video/mpeg, video/webm, video/mov"
            />
            {errors.file?.message && <div className="text-xs text-red-500">{errors.file.message}</div>}
          </div>

          <Label className="mt-4">Image</Label>
          <div className="mt-3">
            {existingImageUrl && !newImageFile && (
              <div className="relative mb-2">
                <img
                  src={`${process.env.REACT_APP_CLOUD_FRONT}${existingImageUrl}`}
                  className="h-auto max-w-xs max-h-40"
                  alt="Current thumbnail"
                />
                <TrashIcon
                  className="absolute w-5 text-white bg-black rounded-full cursor-pointer h-7 top-2 right-2"
                  onClick={handleDeleteImage}
                />
              </div>
            )}
            {newImageFile && (
              <div className="relative mb-2">
                <img
                  src={URL.createObjectURL(newImageFile)}
                  className="h-auto max-w-xs max-h-40"
                  alt="New thumbnail"
                />
                <TrashIcon
                  className="absolute w-5 text-white bg-black rounded-full cursor-pointer h-7 top-2 right-2"
                  onClick={handleDeleteImage}
                />
              </div>
            )}
            <div className="flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md border-neutral-300 dark:border-neutral-6000">
              <div className="space-y-1 text-center">
                <svg
                  className="w-12 h-12 mx-auto text-neutral-400"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 48 48"
                  aria-hidden="true"
                >
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                  <label
                    htmlFor="fileInput"
                    className="relative font-medium rounded-md cursor-pointer text-primary-6000 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500"
                  >
                    <span>Ajouter</span>
                    <input
                      id="fileInput"
                      type="file"
                      className="sr-only"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setValue("image", file);
                          trigger("image");
                        }
                      }}
                      accept="image/jpeg, image/jpg, image/png, image/webp"
                    />
                  </label>
                  <p className="pl-1">ou déposer</p>
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">PNG, JPG, GIF jusqu'à 10MB</p>
              </div>
            </div>
            {errors.image?.message && <div className="text-xs text-red-500">{errors.image.message}</div>}
          </div>

          <CardFooter className="pt-0">
            <Typography variant="small" className="flex justify-center mt-6">
              <ButtonPrimary type="submit" loading={isLoading}>
                Mettre à jour
              </ButtonPrimary>
            </Typography>
          </CardFooter>
        </form>
      </DashboardMenu>
    </div>
  );
};

export default UpdateVideoSliderPage;
